import { memo, useMemo } from 'react';
import {
  Badge,
  Button,
  Text,
} from '@coreai/fluentui-react';
import { useAppStore } from '@/stores/appStore';
import { useTenants, useProviderLocations } from '@/hooks/useAzureResources';
import { useRecentWorkStore } from '@/stores/recentWorkStore';
import { useSettingCardStyles } from './sectionStyles';
import { SearchableCombobox } from '@/components/shared';

interface GeneralSectionProps {
  /** Opens the TenantSwitchDialog. Lifted to the modal so the dialog
   *  itself stays mounted at the root, outside the modal surface. */
  onSwitchTenantClick: () => void;
}

const ALL_REGIONS = '__all__';

/**
 * General workspace preferences: tenant, default region, and local cache
 * actions. Everything here is persisted client-side (Zustand `persist`).
 *
 * The region picker mirrors `RegionFilter` (project list header): same
 * `useProviderLocations` source list, same `Combobox` + freeform search
 * UI, same friendly display names. We don't reuse `RegionFilter`
 * directly because it carries navigation + endpoint side-effects we
 * don't want in Settings (Settings only writes the preference to the
 * store; rerouting happens on the next page navigation).
 */
export const GeneralSection = memo<GeneralSectionProps>(({ onSwitchTenantClick }) => {
  const styles = useSettingCardStyles();
  // Subscribe per-field — see AppearanceSection for rationale.
  const selectedTenantId = useAppStore((s) => s.selectedTenantId);
  const selectedLocation = useAppStore((s) => s.selectedLocation);
  const setSelectedLocation = useAppStore((s) => s.setSelectedLocation);
  const { data: tenants } = useTenants();
  const { data: locations, isLoading: locationsLoading } = useProviderLocations();
  const entries = useRecentWorkStore((s) => s.entries);
  const clearRecent = useRecentWorkStore((s) => s.clearAll);

  const tenantName = tenants?.find((t) => t.tenantId === selectedTenantId)?.displayName ?? 'Not signed in';
  const multiTenant = (tenants?.length ?? 0) > 1;

  // Region picker — the "All regions" sentinel is a real option in the list
  // so the combobox can show it as a selectable item. `SearchableCombobox`
  // handles the type-to-filter, selected-text display, and listbox cap.
  const regionOptions = useMemo(() => {
    const base = [{ value: ALL_REGIONS, text: 'All regions' }];
    if (!locations) return base;
    return [
      ...base,
      ...locations.map((l) => ({
        value: l.name,
        text: l.displayName,
        searchTerms: [l.name],
      })),
    ];
  }, [locations]);

  return (
    <div className={styles.list}>
      <div className={styles.listRow}>
        <div>
          <p className={styles.cardLabel}>Active tenant</p>
          <p className={styles.cardHelp}>
            Tenant scoping affects every API call. Switch when you want to manage projects in another directory.
          </p>
        </div>
        <div className={styles.cardControl}>
          <Badge appearance="tint" color="brand" className={styles.tenantBadge} title={tenantName}>
            {tenantName}
          </Badge>
          {multiTenant && (
            <Button appearance="secondary" size="small" onClick={onSwitchTenantClick}>
              Switch tenant
            </Button>
          )}
        </div>
      </div>

      <div className={styles.listRow}>
        <div>
          <p className={styles.cardLabel}>Default region filter</p>
          <p className={styles.cardHelp}>
            Applied when opening the project list. “All regions” shows projects across every region.
          </p>
        </div>
        <div className={styles.cardControl}>
          <SearchableCombobox
            aria-label="Default region"
            placeholder={locationsLoading ? 'Loading regions…' : 'All regions'}
            options={regionOptions}
            value={selectedLocation ?? ALL_REGIONS}
            onChange={(next) =>
              setSelectedLocation(next === ALL_REGIONS ? null : next)
            }
            disabled={locationsLoading}
            // See AccessibilitySection / AiAssistantSection for the same
            // pattern: inlinePopup keeps the listbox inside the Dialog's
            // focus trap; pinned below-start placement keeps Floating UI
            // from ping-ponging.
            inlinePopup
            positioning="below-start"
            noMatchMessage="No regions found"
          />
        </div>
      </div>

      <div className={styles.listRow}>
        <div>
          <p className={styles.cardLabel}>Recent items</p>
          <p className={styles.cardHelp}>
            Auto remembers your last 20 visited apps, projects and workflows for quick navigation from the dashboard.
          </p>
        </div>
        <div className={styles.cardControl}>
          <Text size={200} className={styles.tabular}>
            {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
          </Text>
          <Button
            appearance="secondary"
            size="small"
            onClick={() => clearRecent()}
            disabled={entries.length === 0}
          >
            Clear recent items
          </Button>
        </div>
      </div>
    </div>
  );
});
GeneralSection.displayName = 'GeneralSection';
